<template>
  <fts-design-modal
    width="1280px"
    class="fts-listing-design-modal"
    ref="ftsDesignModal"
  >
    <!-- title -->
    <template #title>
      <!-- page design (canvas.listDesign, R13 / §5.7): a fixed name, never a view -->
      <div class="design-modal-header-std__title" v-if="carrierMode">
        <fts-emoji :emoji="formData.emoji" :toggle-size="18" :disabled="true" class="design-modal-header-emoji" />
        <span class="design-modal-header-input design-modal-header-input--static">Default</span>
      </div>
      <!-- studio title -->
      <div class="design-modal-header-std__title" v-else>
        <fts-emoji v-model="formData.emoji" :toggle-size="18" class="design-modal-header-emoji" />
        <input
          type="text"
          placeholder="Please enter a name"
          class="fts-form-control design-modal-header-input"
          :class="{ error: $v.formData.name && $v.formData.name.$error }"
          v-model="formData.name"
          spellcheck="false"
          @input="debounceInput"
          ref="designerName"
        />
      </div>
      <!-- error message -->
      <div class="design-modal-form-error" v-if="error('name')">
        {{ error("name") }}
      </div>
    </template>
    <!-- description -->
    <template #description>
      <template v-if="carrierMode">
        <span class="design-modal-header-tag">Page design</span>
        <span class="design-modal-header-description">
          the page everyone sees while the module has no views; views without a card of their own inherit it
        </span>
      </template>
      <template v-else>
        <span class="design-modal-header-tag">Listing</span>
        <span class="design-modal-header-description">
          filter entries and specify information to show different group of users
        </span>
      </template>
    </template>
    <!-- body -->
    <template #body>
      <!-- description -->
      <div class="fts-form-group" v-if="!carrierMode">
        <fts-contenteditable
          v-model="formData.description"
          text-wrap
          placeholder="enter description (optional)"
        />
      </div>
      <!-- filter conditions (scope never enters the page design, R11) -->
      <div class="design-modal-form-legend" v-if="!carrierMode">Filter conditions</div>
      <!-- profile only -->
      <fts-form-element
        :item="{
          fieldName: 'profileOnly',
          input: 'toggleSwitch',
        }"
        :form-data="formData"
        comfort-design
        v-if="!carrierMode"
      />
      <!-- filter conditions -->
      <template v-if="!carrierMode && !formData.profileOnly">
        <div class="fts-form-group">
          <fts-query-designer
            :modules="modules"
            :states="states"
            :information-source="filterSelection()"
            :filter="formData.filter"
          />
          <!-- error message -->
          <div class="design-modal-form-error" v-if="error('filter')">
            {{ error("filter") }}
          </div>
        </div>
        <!-- query script -->
        <div class="fts-form-group" v-if="isBeta">
          <fts-collapsible-element
            :condition="!!formData.queryScript"
            label="query script"
            immersive
            beta
          >
            <fts-script-editor
              slot="content"
              editor-id="queryScript"
              :save-button="!!saveCallBack"
              v-model="formData.queryScript"
              lang="python"
              label="Query Script"
              @save="done(true, true)"
            >
              <template #label="{ label }">
                {{label}} <fts-release-badge class="fts-align-top" />
              </template>
            </fts-script-editor>
          </fts-collapsible-element>
        </div>
      </template>
      <!-- list design -->
      <div class="fts-form-group fts-form-group--comfort">
        <fts-list-designer
          v-model="formData.list"
          :information-source="getColumnsSelection()"
          @input="updateDefaultSelection()"
        />
        <!-- error message -->
        <div class="design-modal-form-error list-design-error" v-if="error('list')">
          {{ error("list") }} Click above and start to customize your list.
        </div>
      </div>
      <!-- list design (mobile) -->
      <div class="fts-form-group dm-unavailable-message" v-if="formData.appList">
        For the App list design, please configure on the App or Builder.
      </div>
      <div class="fts-form-group" v-else>
        <fts-list-designer
          title="App list design (Depreciated)"
          v-model="formData.mobileListDesign"
          :information-source="getColumnsSelection('app')"
          @input="updateDefaultSelection('app')"
        />

        <div class="dm-unavailable-message dm-unavailable-message--warning" style="margin-top: 0.375rem;">
          The current App list design is depreciated. If you wish to configure it, we strongly recommend doing so within the App or Builder.
        </div>
      </div>
      <!-- WIP -->
      <div v-if="isInternal" style="margin-top: 1.5rem;margin-bottom: 2rem;padding: 0.5rem 0.75rem;border: 1px solid var(--fts-border);border-radius: 0.25em;">
        <div class="fts-text-right">
          <fts-release-badge alpha />
        </div>
        <div class="fts-form-group">
          <fts-form-label placement="top">
            List Design
          </fts-form-label>
          <fts-listing-design
            :modules="modules"
            :activities="activities"
            :documents="documents"
            :actions="actions"
            :options="listingWebOptions"
            :list.sync="formData.list.columns"
          />
        </div>
        <div class="fts-form-group" v-if="!formData.appList">
          <fts-form-label placement="top">
            App List Design (Depreciated)
          </fts-form-label>
          <fts-listing-design
            :modules="modules"
            :activities="activities"
            :documents="documents"
            :actions="actions"
            :options="listingAppOptions"
            :list.sync="formData.mobileListDesign.columns"
          />
        </div>
      </div>
      <!-- advanced (default filters, sort, logic: view scope, not page design) -->
      <fts-collapsible-element :condition="advanced" v-if="!carrierMode">
        <fts-btn
          slot="toggle"
          slot-scope="{ isVisible, toggle }"
          text
          text-transform-init
          :class="{ open: isVisible }"
          class="design-modal-advanced-toggle"
          @click="toggle()"
        >
          Advanced settings
          <font-awesome-icon
            :icon="['fas', 'caret-right']"
            class="design-modal-advanced-toggle-icon"
          />
        </fts-btn>
        <div slot="content" class="design-modal-advanced-content fts-row gutters">
          <!-- default filter -->
          <div class="fts-col fts-form-group fts-form-group--comfort">
            <fts-form-label placement="top" icon="fts-icon-filter">
              Default Filters
            </fts-form-label>
            <fts-query-designer
              :modules="modules"
              :states="states"
              :information-source="getDefaultFilterSelection()"
              :filter="formData.defaultFilter"
            />
          </div>
          <!-- default app filter -->
          <div class="fts-col fts-form-group fts-form-group--comfort" v-if="isDevelopment">
            <fts-form-label placement="top" icon="fts-icon-filter">
              Default App Filters <fts-release-badge dev class="fts-align-top" />
            </fts-form-label>
            <fts-query-designer
              :modules="modules"
              :states="states"
              :information-source="getDefaultFilterSelection('app')"
              :filter="formData.defaultAppFilter"
            />
          </div>
          <!-- default sort -->
          <div class="fts-col fts-form-group fts-form-group--comfort" v-if="isInternal">
            <fts-form-label placement="top">
              Sort <fts-release-badge alpha class="fts-align-top" />
            </fts-form-label>
            <fts-sort
              :modules="modules"
              strategy-fixed
              placement="bottom-start"
              :fields="defaultSortSelection"
              v-model="formData.defaultSort"
            />
          </div>
          <!-- logic -->
          <div class="fts-col" v-if="isBeta">
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'logicId',
                displayName: 'Logic',
                icon: 'fts-icon-menu-caret-down',
              }"
              comfort-design
            >
              <template #formLabel="{ label }">
                {{label}} <fts-release-badge beta class="fts-align-top" />
              </template>
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item }"
                :form-data="formData"
                :item="item"
                :options="logicServices"
                comfort-design
              />
            </fts-form-element>
          </div>
          <!-- auto-update -->
          <div class="fts-col" v-if="isInternal">
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'autoUpdate',
                input: 'toggleSwitch'
              }"
              comfort-design
            >
              <template #formLabel="{ label }">
                {{label}} <fts-release-badge alpha class="fts-align-top" />
              </template>
            </fts-form-element>
          </div>
          <!-- include archive -->
          <div class="fts-col" v-if="isBeta">
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'includeArchive',
                input: 'toggleSwitch'
              }"
              comfort-design
            >
              <template #formLabel="{ label }">
                {{label}} <fts-release-badge class="fts-align-top" />
              </template>
            </fts-form-element>
          </div>
        </div>
      </fts-collapsible-element>
    </template>
    <!-- footer left -->
    <template #footerLeft>
      <!-- remove -->
      <fts-btn text icon @click="remove" v-if="isExist">
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
      <!-- auth (the page design carries no rights, R11) -->
      <fts-auth
        type="menu"
        :unit-id="formData.id"
        :title="formData.name"
        preview-placement="top"
        v-if="!carrierMode"
      >
        <fts-btn
          slot="trigger"
          slot-scope="{ show, isRight }"
          text
          icon
          :color="isRight ? 'danger' : 'default'"
          @click="show"
        >
          <font-awesome-icon :icon="['fas', 'lock']" v-if="isRight" />
          <font-awesome-icon :icon="['fas', 'unlock']" v-else />
        </fts-btn>
      </fts-auth>
    </template>
    <!-- footer right -->
    <template #footerRight>
      <!-- cancel -->
      <fts-btn text @click="beforeClose">Cancel</fts-btn>
      <!-- save -->
      <fts-btn color="primary" text @click="done(true)" v-if="Boolean(saveCallBack)">
        <font-awesome-icon :icon="['fas', 'check']" /> Save
      </fts-btn>
      <!-- done -->
      <fts-btn outline color="primary" @click="done">
        <font-awesome-icon :icon="['fas', 'check']" /> Ok
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from 'vue';
import Vuelidate from 'vuelidate';
Vue.use(Vuelidate);
import changesets from 'diff-json';

import { authorizationStore } from '../store/authorizationStore';
import commons from '../commons/commons';
import canvasDesign from '../commons/canvasDesign';
import queryLogic from '../commons/queryLogic';
import validator from '../commons/designValidator';

import FTSDesignModal from './FTSDesignModal';
import FTSAuth from './FTSAuth';
import FTSQueryDesigner from './FTSQueryDesigner';
import FTSListDesigner from './FTSListDesigner';
import FTSFormLabel from '../elements/FTSFormLabel';
import FTSFormElement from '../elements/FTSFormElement';
import FTSEmoji from '../elements/FTSEmoji';
import FTSCollapsibleElement from '../elements/FTSCollapsibleElement';
import FTSScriptEditor from '../design/FTSScriptEditor';
import FTSBtn from '../elements/FTSBtn';
import FTSContenteditable from '../elements/FTSContenteditable';
import FTSSort from '../elements/FTSSort';
import FTSListingDesign from './FTSListingDesign';
import FTSSelect from '../elements/FTSSelect';

export default {
  components: {
    "fts-design-modal": FTSDesignModal,
    "fts-auth": FTSAuth,
    "fts-query-designer": FTSQueryDesigner,
    "fts-list-designer": FTSListDesigner,
    "fts-form-label": FTSFormLabel,
    "fts-form-element": FTSFormElement,
    "fts-emoji": FTSEmoji,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-script-editor": FTSScriptEditor,
    "fts-btn": FTSBtn,
    "fts-contenteditable": FTSContenteditable,
    "fts-sort": FTSSort,
    "fts-listing-design": FTSListingDesign,
    "fts-select": FTSSelect
  },
  data: () => ({
    formData: null,
    originalData: null,
    resolve: null,
    saveCallBack: null,
    advanced: false,
    /* editing the page design (canvas.listDesign) rather than a view: no name, filter, rights or delete */
    carrierMode: false,
  }),
  validations() {
    // The page design has no name or filter, and its columns may stay empty (derived until designed, R5).
    if (this.carrierMode) return { formData: { name: {}, list: { columns: {} }, filter: {} } };
    let formData = {};
    formData = validator.menuDesign(this.menus);
    return { formData };
  },
  methods: {
    async show(listing, saveCallBack) {
      this.saveCallBack = saveCallBack;
      /* carrier: the page design (canvas.listDesign) opened from the console's pinned "Default" row */
      this.carrierMode = !!(listing && listing.carrier);
      this.formData = _.cloneDeep(listing);
      delete this.formData.carrier;

      if (this.isExist) {
        const { t, tid } = this.$route.query;
        if (!t && !tid) {
          commons.addDesignQueryParams(this.$router, this.$route, [
            { name: "t", value: "menu" },
            { name: "tid", value: this.formData.id }
          ]);
        }
      }

      /* filter */
      if (!this.formData.filter) {
        this.$set(this.formData, "filter", { items: [] });
      }
      /* default filter */
      if (!this.formData.defaultFilter) {
        this.$set(this.formData, "defaultFilter", { items: [] });
      } else {
        this.updateDefaultFilter();
      }
      /* default app filter */
      if (!this.formData.defaultAppFilter) {
        this.$set(this.formData, "defaultAppFilter", { items: [] });
      } else {
        this.updateDefaultFilter('app');
      }
      /* sort */
      if (this.formData.defaultSort) {
        this.updateDefaultSort();
      }
      /* expand list design */
      if (this.formData.list && Array.isArray(this.formData.list.columns)) {
        canvasDesign.expandMenuDesign(this.formData.list.columns, this.information);
      } else {
        this.$set(this.formData, "list", { columns: [] });
      }
      /* expand mobile list design */
      if (this.formData.mobileListDesign && Array.isArray(this.formData.mobileListDesign.columns)) {
        canvasDesign.expandMenuDesign(this.formData.mobileListDesign.columns, this.information);
      } else {
        this.$set(this.formData, "mobileListDesign", { columns: [] });
      }

      return new Promise((resolve) => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });
            } else {
              this.reset();
            }
          },
        });

        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
          this.$refs.designerName.focus();
        });
      });
    },
    done(save, enableToast) {
      this.$v.formData.$touch();

      if (this.$v.formData.$invalid) {
        if (enableToast) {
          this.$toasted.error("Configuration error. Please ensure all values are in a proper format.");
        }
        return;
      }

      if (save) {
        this.originalData = _.cloneDeep(this.formData);
        this.saveCallBack && this.saveCallBack(this.formData);
      } else {
        this.resolve && this.resolve(this.formData);
        this.reset();
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    async remove() {
      const res = await this.$confirmation.show({
        type: 'delete',
        title: 'Listing',
        text: this.formData.name,
        textEmoji: this.formData.emoji
      });

      if (res === true) {
        this.resolve && this.resolve(true);
        this.reset();
      }
    },
    reset() {
      this.carrierMode = false;
      const { t, tid } = this.$route.query;
      if (t === "menu" && tid === this.formData.id) {
        commons.removeDesignQueryParams(this.$router, this.$route, ["t", "tid"]);
      }

      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    debounceInput: _.debounce(async function (target) {
      if (this.formData.name) {
        let vm = this;
        this.$store
          .dispatch("ai", {
            type: "emoji",
            text: vm.formData.name,
          })
          .then(function (response) {
            Vue.set(vm.formData, "emoji", response.data);
          });
      }
    }, 800),
    error(key) {
      return validator.menuDesignError(this.$v.formData, key);
    },
    getColumnsSelection(type, withLabel) {
      let differences = ["collaboration", "due"];
      const columns = type === 'app' ?
        this.formData.mobileListDesign && this.formData.mobileListDesign.columns :
        this.formData.list && this.formData.list.columns;

      if (type === 'app') {
        differences = differences.concat(["lastChangeBy", "lastChangeDate"]);
      }

      if (!(columns && columns.some((x) => x.id === 'date' && x.system))) {
        differences.push("date");
      }

      if (withLabel) {
        let options = [
          { label: true, name: 'Standard' },
          ..._.differenceWith(
            queryLogic.querySelectionPrefix,
            this.userCollection ? differences : [...differences, "username"],
            ({ id }, key) => id === key
          ),
          { id: 'action', displayName: 'Action', actions: [], sortable: false, system: true }
        ];
        if (Array.isArray(this.information) && this.information.length) {
          options = [
            { label: true, name: 'Information' },
            ...this.information
          ].concat(options);
        }
        return options;
      } else {
        return this.information.concat(
          _.differenceWith(
            queryLogic.querySelectionPrefix,
            this.userCollection ? differences : [...differences, "username"],
            ({ id }, key) => id === key
          )
        );
      }
    },
    filterSelection(type) {
      let differences = ["date"];

      if (type === 'app') {
        differences = differences.concat(["lastChangeBy", "lastChangeDate"]);
      }

      return _.differenceWith(
        queryLogic.querySelectionPrefix,
        this.userCollection ? [...differences] : [...differences, "username"],
        ({ id }, key) => id === key
      )
        .concat(
          this.information.filter(x => {
            const informationType = commons.informationTypes.find(y => y.id == x.type);
            return informationType && informationType.searchable;
          })
        )
        .concat(queryLogic.querySelectionSuffix);
    },
    getDefaultFilterSelection(type) {
      try {
        const columns = type === 'app' ?
          this.formData.mobileListDesign && this.formData.mobileListDesign.columns :
          this.formData.list && this.formData.list.columns;      
        if (columns && columns.length) {
          return _.intersectionWith(
            this.filterSelection(type),
            [...columns, { id: 'collaboration' }],
            ({ id }, x) => id === x.id
          ).concat(queryLogic.querySelectionSuffix);
        }
      } catch(err) {
        console.error(`getDefaultFilterSelection(${type||'web'})`, err);
      }
      return this.filterSelection(type);
    },
    updateDefaultFilter(type) {
      let defaultFilter = type === 'app' ? this.formData.defaultAppFilter : this.formData.defaultFilter;
      queryLogic.updateQueryFilters(this.getDefaultFilterSelection(type), defaultFilter);
    },
    updateDefaultSort() {
      if (this.formData.defaultSort) {
        if (
          this.formData.list &&
          this.formData.list.columns &&
          this.formData.list.columns.length &&
          this.formData.defaultSort &&
          this.formData.defaultSort.length
        ) {
          this.formData.defaultSort = this.formData.defaultSort.filter(x =>
            this.formData.list.columns.some(y => y.id === x.fieldId)
          );
        }
        else {
          this.$delete(this.formData, "defaultSort");
        }
      }
    },
    updateDefaultSelection(type) {
      if (type === 'app') {
        this.updateDefaultFilter('app');
      } else {
        this.updateDefaultFilter();
        this.updateDefaultSort();
      }
    }
  },
  computed: {
    isBeta() {
      return authorizationStore.getters.isBeta || this.$store.getters.workspaceBeta;
    },
    isDevelopment() {
      return authorizationStore.getters.isDevelopment;
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    moduleName() {
      return this.$store.state.vector.name;
    },
    modules() {
      return this.$store.getters.workspaceModules;
    },
    menus() {
      return this.$store.getters.canvasMenus;
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    activities() {
      return this.$store.getters.canvasActivities;
    },
    documents() {
      return this.$store.getters.canvasDocuments;
    },
    actions() {
      return this.$store.getters.canvasActions;
    },
    states() {
      return this.$store.getters.canvasStates;
    },
    logicServices() {
      return this.$store.getters.canvasLogicServices;
    },
    userCollection() {
      return (
        this.$store.state.vector && this.$store.state.vector.isUserCollection
      );
    },
    defaultSortSelection() {
      return (
        this.formData.list &&
        Array.isArray(this.formData.list.columns) &&
        this.formData.list.columns.filter((x) => x.sortable !== false) 
      );
    },
    isExist() {
      return this.menus.some(x => x.id === this.formData.id);
    },
    listingWebOptions() {
      const list = this.getColumnsSelection('web', true);
      return (
        this.formData &&
        this.formData.list &&
        Array.isArray(this.formData.list.columns)
      ) ? 
        list.filter((x) =>
          !this.formData.list.columns.some((y) => y.id === x.id)
        ) : list;
    },
    listingAppOptions() {
      const list = this.getColumnsSelection('app', true);
      return (
        this.formData &&
        this.formData.mobileListDesign &&
        Array.isArray(this.formData.mobileListDesign.columns)
      ) ?
        list.filter((x) =>
          !this.formData.mobileListDesign.columns.some((y) => y.id === x.id)
        ) : list;
    }
  }
};
</script>

<style lang="scss" scoped>
.fts-listing-design-modal {
  .list-design-error {
    margin-top: 0.5rem;
  }
}
</style>